import React, {useEffect, useMemo, useState} from 'react';
import {type FieldPath, type FieldValues} from 'react-hook-form';
import {getLabelPathFromAutoListItem} from '@netx/core-category/utils';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '@netx/core-components/Form';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils/fn';
import {combineRefs} from '@netx/core-next/utils/react';
import {type ICategoryAutoListItem} from '../../schema';
import {
	CategoryAutoListUnknown,
	type CategoryAutoListUnknownProps,
} from './CategoryAutoListUnknown';

export interface ReactHookFormCategoryAutoListUnknownProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		CategoryAutoListUnknownProps<Multiple, DisableClearable>,
		TFieldValues,
		TName
	> {
	// disableSupplementaryRegister?: boolean;
	// valueLabelName?: string;
	// valuePathName?: string;
}

export const ReactHookFormCategoryAutoListUnknownPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		// disableSupplementaryRegister,
		ControllerProps,
		...props
	}: ReactHookFormCategoryAutoListUnknownProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const fieldName = props.name || 'category-auto-list';

	const [displayControls, setDisplayControls] = useState<{
		label: string | string[];
		path: string | string[];
	}>({
		label: '',
		path: '',
	});

	useEffect(() => {
		setDisplayControls({
			label: '',
			path: '',
		});
	}, [control._defaultValues]);

	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={fieldName as TName}
			render={({field, formState}) => {
				const value = useMemo(
					() =>
						field.value != null
							? props.multiple
								? (field.value as ICategoryAutoListItem[]).map((v, idx) => {
										return {
											value: v,
											label: displayControls.label[idx],
										};
								  })
								: {value: field.value, label: displayControls.label}
							: props.multiple
							? []
							: (null as any),
					[field.value, displayControls.label],
				);

				const handleChange = useConstCallback(
					splitEventHandler(props.onChange, (event, value) => {
						if (props.multiple && Array.isArray(value)) {
							const data = (value as ICategoryAutoListItem[]).reduce<{
								ids: ICategoryAutoListItem['value'][];
								labels: ICategoryAutoListItem['label'][];
								paths: string[];
							}>(
								(accum, v) => {
									accum.ids.push(v.value);
									accum.labels.push(v.label);
									accum.labels.push(getLabelPathFromAutoListItem(v).path || '');
									return accum;
								},
								{
									ids: [],
									labels: [],
									paths: [],
								},
							);
							field.onChange(data.ids);
							setDisplayControls({
								label: data.labels,
								path: data.paths,
							});
						} else {
							const labelPath = getLabelPathFromAutoListItem(
								value as ICategoryAutoListItem,
							);
							field.onChange((value as ICategoryAutoListItem)?.value || 0);
							setDisplayControls({
								label: labelPath.label || '',
								path: labelPath.path || '',
							});
						}
					}),
				);

				return (
					<CategoryAutoListUnknown<Multiple, DisableClearable>
						error={formState.errors[fieldName]?.message?.toString()}
						{...props}
						{...field}
						disabled={field.disabled || props.disabled}
						ref={combineRefs(ref, field.ref)}
						path={
							!props.multiple ? (displayControls.path as string) : undefined
						}
						value={value}
						onChange={handleChange}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

export const ReactHookFormCategoryAutoListUnknown = React.forwardRef(
	ReactHookFormCategoryAutoListUnknownPrimitive,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormCategoryAutoListUnknownProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
) => JSX.Element;

export default ReactHookFormCategoryAutoListUnknown;
